/**
 * The two contracts the web build and the API's crawler pages share.
 *
 * 1. Prerendered markup markers. The web build writes static, crawler-readable
 *    markup into the published HTML (the landing page's copy in index.html,
 *    apps/web/src/prerender) and wraps every such block — and the boot guard it
 *    adds to <head> — in these comments. The API builds its crawler pages from
 *    that same HTML (apps/api/src/seoPages.ts) and strips the marked blocks
 *    first, so a filled index.html can never leak the landing into a novel
 *    page. The web and the API deploy independently, so neither side may
 *    assume the other's version: the markers are the only thing they agree on.
 *
 * 2. Open Graph locales. `og:locale` wants a territory ("en_US"), which a
 *    novel's language code does not carry: each UI language maps to the
 *    territory with the most readers of it.
 */

export const PRERENDER_START = "<!--prerender:start-->";
export const PRERENDER_END = "<!--prerender:end-->";

const PRERENDERED_BLOCK = /<!--prerender:start-->[\s\S]*?<!--prerender:end-->/g;

/** The HTML without any marked prerendered block (idempotent). */
export function stripPrerendered(html: string): string {
  return html.replace(PRERENDERED_BLOCK, "");
}

/** Wraps markup in the prerender markers. */
export function markPrerendered(markup: string): string {
  return `${PRERENDER_START}${markup}${PRERENDER_END}`;
}

export const OG_LOCALES: Readonly<Record<string, string>> = {
  en: "en_US",
  es: "es_ES",
  pt: "pt_BR",
  fr: "fr_FR",
  de: "de_DE",
  ru: "ru_RU",
  zh: "zh_CN",
  ja: "ja_JP"
};

/** The `og:locale` for a language code ("ru", "pt-BR"), or null for a language without one. */
export function ogLocale(language: string | null | undefined): string | null {
  if (!language) return null;
  const primary = language.trim().toLowerCase().split(/[-_]/)[0];
  return OG_LOCALES[primary] ?? null;
}

/** A value usable as `<html lang>` (a BCP 47-shaped tag such as "en" or "pt-BR"), or null. */
export function htmlLang(language: string | null | undefined): string | null {
  const value = language?.trim();
  return value && /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/i.test(value) ? value : null;
}
